import { isErrorUrl } from "@shared/publish-url";

/** The two fields of a publish result these rules read. */
export interface PublishResultState {
  status: string;
  external_url: string | null;
}

/**
 * FEAT-080 / S-350 — a row earns a URL field when it has published nowhere yet.
 * FEAT-107 / S-434 — and when its publish failed: the operator publishes by
 * hand on the platform and records the live link here, instead of a SQL change
 * against production (Substack's `reauthentication_required`, 2026-10-12/13).
 */
export function offersLiveUrl(result: PublishResultState): boolean {
  if (isErrorUrl(result.external_url)) return true;
  return result.status === "pending_manual_publish" || !result.external_url?.trim();
}

/**
 * The label over the URL box. FEAT-112 / S-446 — a result that published but
 * whose link could not be found says so: the article is live, only the link
 * is missing.
 */
export function urlFormLabel(result: PublishResultState): string {
  if (failureReason(result.external_url)) return "Published it by hand? Paste the live URL";
  if (result.status === "published") return "Published \u2014 add the link";
  return "Paste the live URL once you have published it";
}

/** The publisher's message from an `error://<message>` result, or null. */
export function failureReason(externalUrl: string | null): string | null {
  if (!isErrorUrl(externalUrl)) return null;
  const message = (externalUrl ?? "").slice("error://".length).trim();
  return message || null;
}

export type SubstackConfirmOutcome = "saved" | "no_post_id" | "press_kit_failed";

/**
 * FEAT-107 / BUG-128 — what to tell the person after confirming a Substack
 * URL by hand. Null when there is nothing to say (the kit was saved, or the
 * target is not Substack).
 */
export function pressKitNotice(outcome: SubstackConfirmOutcome | undefined): string | null {
  if (outcome === "no_post_id") {
    return "Saved. CCM could not find this post on Substack, so its press-kit images were not fetched — check that the link is the post's own /p/ address.";
  }
  if (outcome === "press_kit_failed") {
    return "Saved. The press-kit images could not be fetched yet — open Amplify later to try again.";
  }
  return null;
}

/**
 * FEAT-111 / S-439 — a failed result earns Retry. FEAT-112 — a LinkedIn
 * article included: the retry rejoins the piece's browser job, so it cannot
 * post the article twice. The role check is the caller's (Editors and Admins).
 */
export function offersRetry(result: PublishResultState & { target_type: string }): boolean {
  return result.status === "error";
}

/** The retry's answer from the server: the result row after the attempt. */
export interface RetryAnswer {
  id: number;
  status: string;
  external_url: string | null;
  external_id: string | null;
}

/**
 * FEAT-111 / S-439 — what to say after Retry. A retry that ran but was refused
 * again answers 200 with status "error"; a refusal to run answers 409 with a
 * code the route names.
 */
export function retryNotice(answer: RetryAnswer | null, refusal?: { error?: string; message?: string }): string {
  if (refusal?.error) {
    if (refusal.error === "piece_running") return "The piece is mid-run. Wait for it to finish, then retry.";
    if (refusal.error === "not_failed") return "This target is no longer failed. Reload to see its latest result.";
    if (refusal.error === "target_disabled") return "This publish target is turned off. Turn it on, then retry.";
    return refusal.message ?? "Retry was refused.";
  }
  if (!answer) return "Retry did not finish. Try again.";
  if (answer.status === "error") return "Retried, and it failed again. The reason is below.";
  if (answer.status === "scheduled") return "Retried: scheduled.";
  return "Retried: published.";
}

/** FEAT-112 / S-444 — `publish_results.progress`, as the API returns it. */
export interface PublishProgress {
  job_id: string;
  steps: Array<{ stage: string; at: string; note?: string }>;
  last_contact_at: string;
  lost_contact?: boolean;
}

/**
 * The browser service's stages in words. Each is recorded as the service
 * enters it, so a label names what that step does, and the card ticks it once
 * the next one starts.
 */
const STEP_LABELS: Record<string, string> = {
  launch: "browser started",
  navigate: "LinkedIn opened",
  session: "signed in",
  editor: "editor opened",
  cover_fetch: "cover downloaded",
  cover: "cover set",
  body: "body pasted",
  save: "draft saved",
  publish: "publish started",
  modal: "publish settings",
  companion: "amplification typed",
  submit: "publishing",
  published: "published",
  url_lookup: "link looked up",
};

/** FEAT-112 / S-444 — a stage in words; one this list does not know shows as it came. */
export function stepLabel(stage: string): string {
  return STEP_LABELS[stage] ?? stage;
}

/** FEAT-112 / S-444 — the browser job is still running; the panel checks again shortly. */
export function isPublishing(result: { status: string }): boolean {
  return result.status === "publishing";
}

export interface StepRow {
  label: string;
  at: string;
  note?: string;
  /** False only for the step under way while the job runs. */
  done: boolean;
}

/** FEAT-112 / S-444 — the card's step list. */
export function stepRows(steps: PublishProgress["steps"] | undefined, publishing: boolean): StepRow[] {
  const list = steps ?? [];
  return list.map((s, i) => ({
    label: stepLabel(s.stage),
    at: s.at,
    note: s.note,
    done: !publishing || i < list.length - 1,
  }));
}
